import { mapApiExamToSection } from "../../../hallticket-lib/spot/spotViewerService";
import type { SpotSeatMatrix, SpotSeatValue } from "./spotRoundTypes";

export const SEAT_HIGHLIGHT_DURATION_MS = 5 * 60 * 1000;

export type SpotUpdatedOnSource = {
  exam?: string;
  category?: string;
  courseName?: string;
  updatedOn?: string;
};

/**
 * Parse backend `updatedOn` as a UTC instant (epoch ms).
 * Naive ISO datetimes (no Z/offset) are treated as UTC.
 */
export function parseUpdatedOnUtcMs(
  updatedOn: string | number | null | undefined,
): number | null {
  if (updatedOn == null || updatedOn === "") {
    return null;
  }

  if (typeof updatedOn === "number" && Number.isFinite(updatedOn)) {
    const ms = updatedOn < 1e12 ? updatedOn * 1000 : updatedOn;
    return Number.isFinite(ms) ? ms : null;
  }

  const raw = String(updatedOn).trim();
  if (!raw) {
    return null;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return null;
  }

  let normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
  const hasZone = /[zZ]$/.test(normalized) || /[+-]\d{2}:?\d{2}$/.test(normalized);
  if (!hasZone && /^\d{4}-\d{2}-\d{2}T/.test(normalized)) {
    normalized = `${normalized}Z`;
  }

  const ms = Date.parse(normalized);
  return Number.isFinite(ms) ? ms : null;
}

/** True when `updatedOn` is within the last 5 minutes of browser `now`. */
export function isUpdatedOnWithinHighlightWindow(updatedOnMs: number, now: number): boolean {
  const ageMs = now - updatedOnMs;
  return ageMs >= 0 && ageMs < SEAT_HIGHLIGHT_DURATION_MS;
}

/** Latest UTC `updatedOn` per seat-highlight key. */
export function collectSeatUpdatedOnByKey(
  spots: SpotUpdatedOnSource[],
): Map<string, number> {
  const next = new Map<string, number>();

  for (const spot of spots) {
    const section = mapApiExamToSection(spot.exam);
    const category = spot.category?.trim();
    const courseName = spot.courseName?.trim();
    if (!section || !category || !courseName) {
      continue;
    }

    const updatedOnMs = parseUpdatedOnUtcMs(spot.updatedOn);
    if (updatedOnMs == null) {
      continue;
    }

    const key = seatHighlightKey(section, category, courseName);
    const existing = next.get(key);
    if (existing == null || updatedOnMs > existing) {
      next.set(key, updatedOnMs);
    }
  }

  return next;
}

/** Latest `updatedOn` across all seat cells (epoch ms). */
export function latestUpdatedOnMs(updatedOnByKey: Map<string, number>): number | null {
  let latest: number | null = null;
  for (const ms of Array.from(updatedOnByKey.values())) {
    if (latest == null || ms > latest) {
      latest = ms;
    }
  }
  return latest;
}

export type SeatCellHighlightMeta = {
  previousValue: SpotSeatValue;
  lastChangedAt: number;
  highlightExpiresAt: number;
};

/** Stable highlight key: courseName + exam + category (matches Public SpotRound fallback). */
export function seatHighlightKey(
  section: "jee" | "mhtcet",
  category: string,
  courseName: string,
): string {
  const exam = section === "jee" ? "JEE" : "MHT-CET";
  return `${courseName}\0${exam}\0${category}`;
}

function normalizeSeatCompareValue(value: SpotSeatValue | undefined): string {
  if (value === "" || value === undefined || value === null) {
    return "";
  }
  return String(value);
}

/** Compare two matrices and return highlight metadata for changed cells. */
export function detectSeatMatrixChanges(
  previous: SpotSeatMatrix,
  next: SpotSeatMatrix,
  now: number,
  existing: Map<string, SeatCellHighlightMeta>,
): { changedKeys: string[]; nextHighlights: Map<string, SeatCellHighlightMeta> } {
  const nextHighlights = new Map(existing);
  const changedKeys: string[] = [];

  for (const section of ["jee", "mhtcet"] as const) {
    const categories = new Set([
      ...Object.keys(previous[section] ?? {}),
      ...Object.keys(next[section] ?? {}),
    ]);

    for (const category of Array.from(categories)) {
      const courseNames = new Set([
        ...Object.keys(previous[section]?.[category] ?? {}),
        ...Object.keys(next[section]?.[category] ?? {}),
      ]);

      for (const courseName of Array.from(courseNames)) {
        const prevValue = previous[section]?.[category]?.[courseName];
        const nextValue = next[section]?.[category]?.[courseName];

        if (normalizeSeatCompareValue(prevValue) === normalizeSeatCompareValue(nextValue)) {
          continue;
        }

        const key = seatHighlightKey(section, category, courseName);
        const highlightExpiresAt = now + SEAT_HIGHLIGHT_DURATION_MS;

        nextHighlights.set(key, {
          previousValue: prevValue ?? "",
          lastChangedAt: now,
          highlightExpiresAt,
        });
        changedKeys.push(key);
        console.log("[SpotRoundHighlight] cell marked as updated", {
          key,
          section,
          category,
          courseName,
          previousValue: prevValue ?? "",
          nextValue: nextValue ?? "",
          highlightExpiresAt,
          expiresInMs: highlightExpiresAt - now,
        });
      }
    }
  }

  return { changedKeys, nextHighlights };
}

/** Drop highlights whose expiry timestamp has passed. Returns removed keys. */
export function expireSeatHighlights(
  highlights: Map<string, SeatCellHighlightMeta>,
  now: number,
): { next: Map<string, SeatCellHighlightMeta>; expiredKeys: string[] } {
  const next = new Map(highlights);
  const expiredKeys: string[] = [];

  for (const [key, meta] of Array.from(highlights.entries())) {
    if (now >= meta.highlightExpiresAt) {
      next.delete(key);
      expiredKeys.push(key);
    }
  }

  return { next, expiredKeys };
}

export type TickerSeatHighlightMeta = {
  previousTotal: number;
  lastChangedAt: number;
  highlightExpiresAt: number;
};

export function tickerHighlightKey(item: {
  collegeId: string;
  college_code: string;
  college_name: string;
}): string {
  const collegeId = item.collegeId?.trim();
  if (collegeId) {
    return collegeId;
  }
  const code = item.college_code?.trim();
  if (code) {
    return code;
  }
  return item.college_name.trim().toLowerCase();
}

export function markTickerSeatHighlight(
  existing: Map<string, TickerSeatHighlightMeta>,
  key: string,
  previousTotal: number,
  now: number,
): Map<string, TickerSeatHighlightMeta> {
  const next = new Map(existing);
  next.set(key, {
    previousTotal,
    lastChangedAt: now,
    highlightExpiresAt: now + SEAT_HIGHLIGHT_DURATION_MS,
  });
  return next;
}

/** Compare ticker totals and return highlight metadata for changed colleges. */
export function detectTickerSeatTotalChanges(
  previousTotalsByKey: Map<string, number>,
  nextItems: Array<{ collegeId: string; college_code: string; college_name: string; total_available_seats: number }>,
  now: number,
  existing: Map<string, TickerSeatHighlightMeta>,
): { changedKeys: string[]; nextHighlights: Map<string, TickerSeatHighlightMeta> } {
  let nextHighlights = new Map(existing);
  const changedKeys: string[] = [];

  for (const item of nextItems) {
    const key = tickerHighlightKey(item);
    const previousTotal = previousTotalsByKey.get(key);
    if (previousTotal === undefined) {
      continue;
    }
    if (previousTotal === item.total_available_seats) {
      continue;
    }

    nextHighlights = markTickerSeatHighlight(
      nextHighlights,
      key,
      previousTotal,
      now,
    );
    changedKeys.push(key);
  }

  return { changedKeys, nextHighlights };
}

/** Drop ticker highlights whose expiry timestamp has passed. */
export function expireTickerHighlights(
  highlights: Map<string, TickerSeatHighlightMeta>,
  now: number,
): { next: Map<string, TickerSeatHighlightMeta>; expiredKeys: string[] } {
  const next = new Map(highlights);
  const expiredKeys: string[] = [];

  for (const [key, meta] of Array.from(highlights.entries())) {
    if (now >= meta.highlightExpiresAt) {
      next.delete(key);
      expiredKeys.push(key);
    }
  }

  return { next, expiredKeys };
}

export function isTickerHighlightActive(
  highlights: Map<string, TickerSeatHighlightMeta>,
  key: string,
  now: number,
): boolean {
  const meta = highlights.get(key);
  return meta != null && now < meta.highlightExpiresAt;
}

/** Prefer backend `updatedOn` when recent; otherwise poll-diff. */
export function isTickerSeatsRecentlyUpdated(
  item: { collegeId: string; college_code: string; college_name: string },
  tickerHighlightMap: Map<string, TickerSeatHighlightMeta>,
  tickerUpdatedOnByCollege: Map<string, number>,
  now: number,
): boolean {
  const key = tickerHighlightKey(item);
  const collegeId = item.collegeId?.trim();
  const updatedOnMs = tickerUpdatedOnByCollege.get(key)
    ?? (collegeId ? tickerUpdatedOnByCollege.get(collegeId) : undefined);
  if (updatedOnMs != null && isUpdatedOnWithinHighlightWindow(updatedOnMs, now)) {
    return true;
  }
  return isTickerHighlightActive(tickerHighlightMap, key, now);
}
